import { Component } from '@angular/core';
import { NavParams, ViewController } from 'ionic-angular';
import { DateTime } from 'luxon';
import { AppConfig } from '../../../app/app.config';
import { CyclesPageData, CycleSummary } from '../../../models/cycles/cycle.models';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { CyclesService } from '../../../services/cycles/cycles.service';

@Component({
  selector: 'cycle-editor-modal',
  templateUrl: 'cycle-editor-modal.html'
})
export class CycleEditorModalComponent {
  config = AppConfig.settings;
  mode: 'activate' | 'end' | 'edit';
  date: PtLuxonDate;
  startDate: PtLuxonDate;
  endDate: PtLuxonDate;
  baseDate: any;
  ordinal: number;
  canChooseOrdinal: boolean;
  previousCycle: CycleSummary;
  nextCycle: CycleSummary;
  activeCycle: CycleSummary;
  canEditStart = false;
  canEditEnd = false;
  ordinalError: string;
  dateError: string;
  startDateError: string;
  endDateError: string;
  submitError: string;
  saving = false;
  loadingProject = false;
  private cycles: CycleSummary[] = [];
  private activationProjectId: number;
  private projectRequest = 0;
  savingAction: 'activate' | 'deactivate' | 'next' | 'edit';

  constructor(
    params: NavParams,
    private viewController: ViewController,
    private cyclesService: CyclesService
  ) {
    this.mode = params.get('mode');
    this.cycles = params.get('cycles') || [];
    const dailyLogDate = params.get('dailyLogDate') as PtLuxonDate;
    this.baseDate = dailyLogDate.dateTime;
    this.date = new PtLuxonDate(dailyLogDate.dateTime);
    this.ordinal = params.get('suggestedOrdinal');
    this.canChooseOrdinal = params.get('canChooseOrdinal');
    this.previousCycle = params.get('previousCycle');
    this.nextCycle = params.get('nextCycle');
    this.activeCycle = params.get('activeCycle');
    this.canEditStart = params.get('canEditStart') === true;
    this.canEditEnd = params.get('canEditEnd') === true;
    if (this.mode === 'edit') {
      this.startDate = new PtLuxonDate(this.activeCycle.startDateUtc);
      this.endDate = this.activeCycle.endDateUtc ? new PtLuxonDate(this.activeCycle.endDateUtc) : null;
    }
  }

  ionViewDidEnter() {
    if (this.mode === 'activate') this.refreshActivationProject();
  }

  private async refreshActivationProject(): Promise<boolean> {
    const request = ++this.projectRequest;
    if (!this.date || !this.date.dateTime || !this.date.dateTime.isValid || !this.date.offsetNotNull()) {
      this.loadingProject = false;
      return false;
    }
    this.loadingProject = true;
    try {
      const setup = await this.cyclesService.getActivationProject(this.date.dateTime);
      if (request !== this.projectRequest) return false;
      const projectId = setup && setup.projectId;
      if (projectId == null) {
        this.activationProjectId = undefined;
        this.previousCycle = null;
        this.submitError = 'There is no active project at the selected cycle start time.';
        return false;
      }
      const previous = this.cycles.filter(cycle => cycle.projectId === projectId)
        .sort((a, b) => b.ordinal - a.ordinal)[0];
      if (projectId !== this.activationProjectId || previous) {
        this.ordinal = previous ? previous.ordinal + 1 : 1;
      }
      this.activationProjectId = projectId;
      this.previousCycle = previous || null;
      this.canChooseOrdinal = !previous;
      this.submitError = null;
      return true;
    } catch (error) {
      if (request === this.projectRequest) {
        this.submitError = this.getErrorMessage(error, 'The project could not be loaded for this start time.');
      }
      return false;
    } finally {
      if (request === this.projectRequest) this.loadingProject = false;
    }
  }

  get title(): string {
    if (this.mode === 'activate') return 'Activate cycle';
    if (this.mode === 'end') return `End cycle ${this.activeCycle.ordinal}`;
    return `Edit cycle ${this.activeCycle.ordinal}`;
  }

  async save(startNextCycle: boolean = false) {
    if (this.saving || this.loadingProject) {
      return;
    }

    if (this.mode === 'activate' && !await this.refreshActivationProject()) return;

    this.ordinalError = null;
    this.submitError = null;

    if (this.mode === 'activate' && (!Number.isInteger(Number(this.ordinal)) || Number(this.ordinal) < 1)) {
      this.ordinalError = 'Enter a whole cycle number greater than zero.';
      return;
    }

    if (this.mode === 'edit' ? !this.validateEditDates() : !this.validateDateTime()) {
      return;
    }

    this.saving = true;
    this.savingAction = this.mode === 'activate' ? 'activate' :
      this.mode === 'edit' ? 'edit' : startNextCycle ? 'next' : 'deactivate';

    try {
      let data: CyclesPageData;
      if (this.mode === 'activate') {
        data = await this.cyclesService.activate(Number(this.ordinal), this.date.dateTime, this.baseDate);
      } else if (this.mode === 'end') {
        data = await this.cyclesService.end(this.activeCycle.id, this.date.dateTime, this.baseDate, startNextCycle);
      } else {
        data = await this.cyclesService.update(
          this.activeCycle.id,
          this.canEditStart ? this.startDate.dateTime : null,
          this.canEditEnd ? this.endDate.dateTime : null,
          this.baseDate);
      }

      this.viewController.dismiss({
        confirmed: true,
        ordinal: Number(this.ordinal),
        date: this.date,
        startNextCycle: startNextCycle,
        data: data
      } as CycleEditorResult);
    } catch (error) {
      this.submitError = this.getErrorMessage(
        error,
        this.mode === 'activate' ? 'The cycle could not be activated.' :
          this.mode === 'end' ? 'The cycle could not be ended.' : 'The cycle could not be updated.'
      );
      this.saving = false;
      this.savingAction = null;
    }
  }

  validateDateTime(): boolean {
    this.dateError = null;

    if (!this.date || !this.date.dateTime || !this.date.dateTime.isValid || !this.date.offsetNotNull()) {
      this.dateError = 'Enter a valid date, time and time-zone offset.';
      return false;
    }

    const selectedUtcMillis = this.date.dateTime.toUTC().toMillis();
    if (!this.isDailyLogDate(this.date.dateTime)) {
      this.dateError = 'Open the Daily Log day containing this cycle change before changing it.';
      return false;
    }

    if (selectedUtcMillis > DateTime.utc().toMillis()) {
      this.dateError = this.mode === 'activate'
        ? 'Start date and time cannot be in the future.'
        : 'End date and time cannot be in the future.';
      return false;
    }

    if (this.mode === 'end' && selectedUtcMillis <= this.activeCycle.startDateUtc.toUTC().toMillis()) {
      this.dateError = 'End date and time must be after the cycle start.';
      return false;
    }

    if (this.mode === 'activate' && this.previousCycle && this.previousCycle.endDateUtc &&
      selectedUtcMillis < this.previousCycle.endDateUtc.toUTC().toMillis()) {
      this.dateError = 'Start date and time cannot be before the previous cycle ended.';
      return false;
    }

    return true;
  }

  validateEditDates(): boolean {
    this.startDateError = null;
    this.endDateError = null;

    if (this.canEditStart && !this.validateEditableDate(this.startDate, true)) return false;
    if (this.canEditEnd && !this.validateEditableDate(this.endDate, false)) return false;

    const proposedStart = this.startDate.dateTime.toUTC().toMillis();
    const proposedEnd = this.endDate ? this.endDate.dateTime.toUTC().toMillis() : null;
    if (proposedEnd != null && proposedEnd <= proposedStart) {
      this.endDateError = 'End date and time must be after the cycle start.';
      return false;
    }

    const oldStart = this.activeCycle.startDateUtc.toUTC().toMillis();
    const sharesPreviousBoundary = this.previousCycle && this.previousCycle.endDateUtc &&
      this.previousCycle.endDateUtc.toUTC().toMillis() === oldStart;
    if (this.canEditStart && this.previousCycle && this.previousCycle.endDateUtc && !sharesPreviousBoundary &&
      proposedStart < this.previousCycle.endDateUtc.toUTC().toMillis()) {
      this.startDateError = 'Start date and time cannot be before the previous cycle ended.';
      return false;
    }
    if (this.canEditStart && sharesPreviousBoundary && proposedStart <= this.previousCycle.startDateUtc.toUTC().toMillis()) {
      this.startDateError = 'Start date and time must be after the previous cycle started.';
      return false;
    }

    const oldEnd = this.activeCycle.endDateUtc ? this.activeCycle.endDateUtc.toUTC().toMillis() : null;
    const sharesNextBoundary = this.nextCycle && oldEnd != null &&
      this.nextCycle.startDateUtc.toUTC().toMillis() === oldEnd;
    if (this.canEditEnd && this.nextCycle && !sharesNextBoundary &&
      proposedEnd > this.nextCycle.startDateUtc.toUTC().toMillis()) {
      this.endDateError = 'End date and time cannot be after the next cycle starts.';
      return false;
    }
    if (this.canEditEnd && sharesNextBoundary && this.nextCycle.endDateUtc &&
      proposedEnd >= this.nextCycle.endDateUtc.toUTC().toMillis()) {
      this.endDateError = 'End date and time must be before the next cycle ends.';
      return false;
    }

    return true;
  }

  private validateEditableDate(value: PtLuxonDate, start: boolean): boolean {
    if (!value || !value.dateTime || !value.dateTime.isValid || !value.offsetNotNull()) {
      this.setEditError(start, 'Enter a valid date, time and time-zone offset.');
      return false;
    }
    if (!this.isDailyLogDate(value.dateTime)) {
      this.setEditError(start, 'Open the Daily Log day containing this cycle change before changing it.');
      return false;
    }
    if (value.dateTime.toUTC().toMillis() > DateTime.utc().toMillis()) {
      this.setEditError(start, `${start ? 'Start' : 'End'} date and time cannot be in the future.`);
      return false;
    }
    return true;
  }

  private setEditError(start: boolean, message: string) {
    if (start) this.startDateError = message;
    else this.endDateError = message;
  }

  private isDailyLogDate(selectedDate: DateTime): boolean {
    const selectedDay = DateTime.utc(selectedDate.year, selectedDate.month, selectedDate.day);
    const dailyLogDay = DateTime.utc(this.baseDate.year, this.baseDate.month, this.baseDate.day);
    return selectedDay.toMillis() === dailyLogDay.toMillis();
  }

  async dateTimeChanged(date: PtLuxonDate) {
    this.date = date;
    if (this.mode === 'activate' && !await this.refreshActivationProject()) return;
    this.validateDateTime();
  }

  startDateTimeChanged(date: PtLuxonDate) {
    this.startDate = date;
    this.validateEditDates();
  }

  endDateTimeChanged(date: PtLuxonDate) {
    this.endDate = date;
    this.validateEditDates();
  }

  cancel() {
    if (this.saving) {
      return;
    }

    this.viewController.dismiss({confirmed: false} as CycleEditorResult);
  }

  private getErrorMessage(error: any, fallback: string): string {
    const apiError = error && error.error;
    return typeof apiError === 'string'
      ? apiError
      : apiError && apiError.message ? apiError.message : fallback;
  }
}

export interface CycleEditorResult {
  confirmed: boolean;
  ordinal?: number;
  date?: PtLuxonDate;
  startNextCycle?: boolean;
  data?: CyclesPageData;
}



// WEBPACK FOOTER //
// ./src/pages/cycles/cycle-editor-modal/cycle-editor-modal.ts